import { Select, type SelectOptionType } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { CollaborationStatus } from 'client/gql/graphql';
import { Form, Formik } from 'formik';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';

const CreateCollaborationMutationDocument = graphql(`
  mutation CreateCollaborationInColaborationsForm($uuid: ID!) {
    createCollaboration(uuid: $uuid) {
      uuid
      __typename
    }
  }
`);

export const CollaborationsFormQueryDocument = graphql(`
  query CollaborationsFormData($organizationUuid: ID!) {
    collaboratingOrganizations(uuid: $organizationUuid) {
      uuid
      __typename
      name
      collaborationStatus
    }
  }
`);

type CollaborationsFormType = {
  uuid: string;
  name: string;
};

type CollaborationsFormProps = {
  onClose: () => void;
};

export const CollaborationsForm = ({ onClose }: CollaborationsFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();

  const [{ data }] = useQuery({
    query: CollaborationsFormQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
    },
  });

  const collaboratingOrganizations = data?.collaboratingOrganizations ?? [];

  const permissions = usePermissionChecker();

  const [, createCollaboration] = useMutation(CreateCollaborationMutationDocument);

  const initialValues: CollaborationsFormType = {
    uuid: '',
    name: '',
  };

  const selectOpts = useCreateSelectOptions(
    collaboratingOrganizations.filter((collab) => {
      const filteredStates = [
        CollaborationStatus.NoCollaborationPossible,
        CollaborationStatus.Pending,
        CollaborationStatus.Collaboration,
      ];
      return !filteredStates.includes(collab.collaborationStatus as CollaborationStatus);
    }),
    'uuid',
    'name',
  );

  const onSubmit = async (values: CollaborationsFormType) => {
    if (values.uuid !== '' && permissions?.canCreateCollaboration({ uuid: organizationUuid })) {
      const resp = await createCollaboration({ uuid: values.uuid });

      if (resp.error) {
        showErrorToast(resp.error);
      } else {
        showSuccessToast(t('common.saved'));
        onClose();
      }
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit}>
      {({ resetForm, setFieldValue, handleBlur, values, isSubmitting, errors }) => {
        return (
          <Form>
            <Select
              label={t('collaborations.cooperationSchool')}
              name={'uuid'}
              onBlur={handleBlur}
              isSearchable
              onChange={(ev) => setFieldValue('uuid', (ev as unknown as SelectOptionType).value)}
              defaultValue={{
                value: values.uuid ?? '',
                label: values.name ?? '',
              }}
              options={selectOpts}
              menuPosition='fixed'
            />

            <ModalBottomButtons
              closeButton={{
                callback: () => {
                  resetForm();
                  onClose();
                },
              }}
              submitButton={{ text: t('common.sendRequest') }}
              errors={errors}
              isLoading={isSubmitting}
            />
          </Form>
        );
      }}
    </Formik>
  );
};
